import {
  BuyNowInitializeOptions,
  InitialProps,
  SmartPaymentButton,
  SmartPaymentButtonInitializationProps,
} from '../../types';
import ShowMoreButtonConstructor from '../ShowMoreButton/ShowMoreButton';
import SmartPaymentButtonConstructor from '../SmartPaymentButton/SmartPaymentButton';

export default class App {
  private parentElement?: HTMLElement | null;

  constructor(
    private smartPaymentButtonConstructor: SmartPaymentButtonConstructor,
    private showMoreButtonConstructor: ShowMoreButtonConstructor,
  ) {}

  async initialize(props: InitialProps): Promise<void> {
    const {
      buyNowInitializeOptions,
      containerId,
      currencyCode,
      showMoreButtonLabel,
      smartPaymentButtons,
      storeSettings,
      visibleBaseButtonsCount,
      confirmationPageRedirectUrl,
      requiresShipping,
    } = props;

    if (!containerId) {
      throw new Error('Required "containerId" is not provided to initializeSmartPaymentButtons');
    }

    this.parentElement = document.getElementById(containerId);

    if (!this.parentElement) {
      throw new Error('Element with provided "containerId" is not defined');
    }

    if (smartPaymentButtons.length === 0) {
      return;
    }

    await this.smartPaymentButtonConstructor.loadCheckoutButtonInitializer(storeSettings.host);

    if (visibleBaseButtonsCount && visibleBaseButtonsCount < smartPaymentButtons.length) {
      this.renderSmartPaymentButtons(
        smartPaymentButtons.slice(0, visibleBaseButtonsCount),
        buyNowInitializeOptions,
        currencyCode,
        confirmationPageRedirectUrl,
        requiresShipping,
      );

      const onShowMoreButtonClick = () => {
        this.renderSmartPaymentButtons(
          smartPaymentButtons.slice(visibleBaseButtonsCount, smartPaymentButtons.length),
          buyNowInitializeOptions,
          currencyCode,
          confirmationPageRedirectUrl,
          requiresShipping,
        );
      };

      this.renderShowMoreButton(onShowMoreButtonClick, showMoreButtonLabel);

      return;
    }

    this.renderSmartPaymentButtons(
      smartPaymentButtons,
      buyNowInitializeOptions,
      currencyCode,
      confirmationPageRedirectUrl,
      requiresShipping,
    );
  }

  renderSmartPaymentButtons(
    smartPaymentButtons: SmartPaymentButton[],
    buyNowInitializeOptions?: BuyNowInitializeOptions,
    currencyCode?: string,
    confirmationPageRedirectUrl?: string,
    requiresShipping?: boolean,
  ): void {
    smartPaymentButtons.map((smartPaymentButton) =>
      this.renderSmartPaymentButton({
        ...smartPaymentButton,
        buyNowInitializeOptions,
        currencyCode,
        confirmationPageRedirectUrl,
        requiresShipping,
      }),
    );
  }

  renderSmartPaymentButton(props: SmartPaymentButtonInitializationProps): void {
    const { methodId } = props;
    const smartPaymentButtonContainer =
      this.smartPaymentButtonConstructor.createButtonContainer(methodId);

    this.appendElement(smartPaymentButtonContainer);

    this.smartPaymentButtonConstructor.initializeButton(props);
  }

  renderShowMoreButton(onClickCallback: () => void, buttonLabel = 'More payment options') {
    const showMoreButton = this.showMoreButtonConstructor.createButton(buttonLabel);

    showMoreButton.addEventListener('click', () => {
      onClickCallback();

      this.removeElement(showMoreButton);
    });

    this.appendElement(showMoreButton);
  }

  appendElement(elementToAppend: HTMLElement): void {
    const parentElement = this.getParentElement();

    parentElement.appendChild(elementToAppend);
  }

  removeElement(elementToRemove: HTMLElement): void {
    const parentElement = this.getParentElement();

    parentElement.removeChild(elementToRemove);
  }

  getParentElement(): HTMLElement {
    if (!this.parentElement) {
      throw new Error('Element with provided "containerId" is not defined');
    }

    return this.parentElement;
  }
}
